import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { Chainage, Crossing } from "./types";

interface Props {
  open: boolean;
  mode: "chainage" | "crossing";
  editing?: Chainage | Crossing | null;
  onClose: () => void;
  onSave: (value: Chainage | Crossing) => void;
}

const defaultChainage: Chainage = {
  id: Date.now(),

  startChainageKm: "",
  startChainageM: "",

  endChainageKm: "",
  endChainageM: "",

  side: "Left-Hand Side",

  chainageLength: "",

  cableDetails: "",
};

const defaultCrossing: Crossing = {
  id: Date.now(),

  crossingChainageKm: "",
  crossingChainageM: "",

  chainageLength: "",

  cableDetails: "",
};

const AddChainageModal: React.FC<Props> = ({
  open,
  mode,
  editing,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<any>(defaultChainage);
  const [errors, setErrors] = useState<any>({});
  const [initialForm, setInitialForm] = useState<any>(null);

  useEffect(() => {
    let data;

    if (editing) {
      data = { ...editing };
    } else {
      data =
        mode === "chainage"
          ? {
              ...defaultChainage,
              id: Date.now(),
            }
          : {
              ...defaultCrossing,
              id: Date.now(),
            };
    }

    setForm({ ...data });
    setInitialForm({ ...data });
    setErrors({});
  }, [editing, open, mode]);

  const handleChange = (key: keyof Chainage, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleChangeCrossing = (key: keyof Crossing, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const hasChanges = useMemo(() => {
    if (!editing) return true;

    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm, editing]);

  const validate = () => {
    const obj: any = {};

    if (mode === "chainage") {
      const startM = Number(form.startChainageM);
      const endM = Number(form.endChainageM);
      const startKm = Number(form.startChainageKm);
      const endKm = Number(form.endChainageKm);

      if (!form.startChainageKm) {
        obj.startChainageKm = "Start Km is required";
      } else if (startKm > 9999) {
        obj.startChainageKm = "Kilometers cannot exceed 9999";
      }

      if (!form.startChainageM) {
        obj.startChainageM = "Start m is required";
      } else if (startM > 999) {
        obj.startChainageM = "Meters cannot exceed 999";
      }

      if (!form.endChainageKm) {
        obj.endChainageKm = "End Km is required";
      } else if (endKm > 9999) {
        obj.endChainageKm = "Kilometers cannot exceed 9999";
      }

      if (!form.endChainageM) {
        obj.endChainageM = "End m is required";
      } else if (endM > 999) {
        obj.endChainageM = "Meters cannot exceed 999";
      }

      if (!form.side) obj.side = "Side is required";
    } else {
      const crossingM = Number(form.crossingChainageM);

      if (!form.crossingChainageKm) {
        obj.crossingChainageKm = "Crossing Km is required";
      } else if (form.crossingChainageKm > 9999) {
        obj.crossingChainageKm = "Kilometers cannot exceed 9999";
      }

      if (!form.crossingChainageM) {
        obj.crossingChainageM = "Crossing m is required";
      } else if (crossingM > 999) {
        obj.crossingChainageM = "Meters cannot exceed 999";
      }
    }

    if (!form.chainageLength) {
      obj.chainageLength = "Chainage Length is required";
    } else if (form.chainageLength > 9999) {
      obj.chainageLength = "Chainage Length cannot exceed 9999";
    }

    if (!form.cableDetails?.trim())
      obj.cableDetails = "Cable details are required";

    setErrors(obj);

    return Object.keys(obj).length === 0;
  };

  const isFormValid =
    mode === "chainage"
      ? form.startChainageKm &&
        form.startChainageM &&
        form.endChainageKm &&
        form.endChainageM &&
        form.chainageLength &&
        form.cableDetails?.trim()
      : form.crossingChainageKm &&
        form.crossingChainageM &&
        form.chainageLength &&
        form.cableDetails?.trim();

  const handleSubmit = () => {
    if (!validate()) return;

    onSave({
      ...form,
      id: editing?.id ?? form?.id,
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {mode === "crossing"
          ? "Add Across Chainage Details"
          : "Add Along Chainage Details"}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {" "}
        {mode === "chainage" ? (
          <>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Start Chainage <span className="required">*</span>
            </Typography>

            <Box display="flex" gap={2} mb={3}>
              <TextField
                fullWidth
                label="Km"
                value={form.startChainageKm}
                onChange={(e) =>
                  handleChange(
                    "startChainageKm",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                error={!!errors.startChainageKm}
                helperText={errors.startChainageKm}
              />

              <TextField
                fullWidth
                label="m"
                value={form.startChainageM}
                onChange={(e) =>
                  handleChange(
                    "startChainageM",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                error={!!errors.startChainageM}
                helperText={errors.startChainageM}
              />
            </Box>

            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              End Chainage <span className="required">*</span>
            </Typography>

            <Box display="flex" gap={2} mb={3}>
              <TextField
                fullWidth
                label="Km"
                value={form.endChainageKm}
                onChange={(e) =>
                  handleChange(
                    "endChainageKm",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                error={!!errors.endChainageKm}
                helperText={errors.endChainageKm}
              />

              <TextField
                fullWidth
                label="m"
                value={form.endChainageM}
                onChange={(e) =>
                  handleChange(
                    "endChainageM",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                error={!!errors.endChainageM}
                helperText={errors.endChainageM}
              />
            </Box>

            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Side <span className="required">*</span>
            </Typography>

            <RadioGroup
              row
              value={form.side}
              onChange={(e) => handleChange("side", e.target.value)}
              sx={{ mb: 3 }}
            >
              <FormControlLabel
                value="Left-Hand Side"
                control={
                  <Radio
                    sx={{
                      color: "#3A1C73",
                      "&.Mui-checked": { color: "#3A1C73" },
                    }}
                  />
                }
                label="Left-Hand Side"
              />

              <FormControlLabel
                value="Right-Hand Side"
                control={
                  <Radio
                    sx={{
                      color: "#3A1C73",
                      "&.Mui-checked": { color: "#3A1C73" },
                    }}
                  />
                }
                label="Right-Hand Side"
              />
            </RadioGroup>
          </>
        ) : (
          <>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Crossing Chainage <span className="required">*</span>
            </Typography>

            <Box display="flex" gap={2} mb={3}>
              <TextField
                fullWidth
                label="Km"
                value={form.crossingChainageKm}
                onChange={(e) =>
                  handleChangeCrossing(
                    "crossingChainageKm",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                error={!!errors.crossingChainageKm}
                helperText={errors.crossingChainageKm}
              />

              <TextField
                fullWidth
                label="m"
                value={form.crossingChainageM}
                onChange={(e) =>
                  handleChangeCrossing(
                    "crossingChainageM",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                error={!!errors.crossingChainageM}
                helperText={errors.crossingChainageM}
              />
            </Box>
          </>
        )}
        {/* Chainage Length */}
        <Typography
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Chainage Length (Km) <span className="required">*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter Chainage Length"
          value={form.chainageLength}
          onChange={(e) =>
            handleChange(
              "chainageLength",
              e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"),
            )
          }
          error={!!errors.chainageLength}
          helperText={errors.chainageLength}
          sx={{
            mb: 3,
          }}
        />
        {/* Cable Details */}
        <Typography
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Number of Cables and Diameter of Protection Pipe{" "}
          <span className="required">*</span>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Enter cable details"
          value={form.cableDetails}
          onChange={(e) => handleChange("cableDetails", e.target.value)}
          error={!!errors.cableDetails}
          helperText={errors.cableDetails}
        />
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || (editing && !hasChanges) || false}
          sx={{
            background: "#3a1c73",
            textTransform: "none",
            borderRadius: "20px",
            p: 1.5,
            "&:hover": {
              background: "#3a1c73",
            },
            "&.Mui-disabled": {
              background: "#D0D5DD",
              color: "#FFFFFF",
            },
          }}
          fullWidth
        >
          {editing ? "Update Chainage" : "Add Chainage"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddChainageModal;
